"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.IMPORT_COLUMNS = exports.IMPORT_MAX_ERRORS = exports.importResultSchema = exports.importErrorSchema = exports.importRequestSchema = exports.IMPORT_SYNC_LIMIT = exports.IMPORT_MAX_BYTES = exports.IMPORT_DOMAINS = void 0;
exports.importField = importField;
exports.importSample = importSample;
const zod_1 = require("zod");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Imports — C17.                                                            │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `main.jsx` 290–348, `markup.html`'s five-stage dialog. Two domains, which are the mockup's own:
 * tasks and time entries.
 */
/**
 * ⚠️ The mockup's two, and no more. Its own dialog offers `tasks` and `time`; the other four export
 * domains have no import because nothing in the design asks for one — a client list arrives through
 * the client screen, and inventing an importer for it would be inventing a workflow.
 */
exports.IMPORT_DOMAINS = ['task', 'worklog'];
/**
 * ⚠️⚠️ **THE FILE ARRIVES AS TEXT IN JSON, NOT AS A MULTIPART UPLOAD.** A CSV *is* text, the export
 * already returns it that way, and multipart would mean a body parser and a temp-file story for no
 * gain. The cost is stated: the whole file is in memory twice (request body and parsed rows), which
 * is why `IMPORT_MAX_BYTES` exists rather than being left to the reverse proxy to discover.
 */
exports.IMPORT_MAX_BYTES = 5_000_000;
/**
 * ⚠️⚠️ **AND THE ROW CEILING AT WHICH IT STOPS BEING SYNCHRONOUS.** The mockup switches to its
 * background stage past 400 rows — a prototype's number, chosen so its demo could show the stage.
 * Ours is 2,000, measured differently: each row is a service call in its own transaction, so 2,000
 * rows is a few seconds, and past that a request is holding a connection long enough to matter.
 */
exports.IMPORT_SYNC_LIMIT = 2_000;
exports.importRequestSchema = zod_1.z.object({
    domain: zod_1.z.enum(exports.IMPORT_DOMAINS),
    /** The file's text. */
    csv: zod_1.z.string().max(exports.IMPORT_MAX_BYTES),
    /**
     * The name, for the report and the email.
     *
     * ⚠️ Not trusted as a path: it is only ever echoed back, never opened. `../../etc/passwd` is a
     * harmless string here and would be a hole the moment anything wrote a file.
     */
    filename: zod_1.z.string().max(200),
    /**
     * P2-32.2 · the line item a new task lands on when its row names none. Every task needs one, and
     * a file of thirty tasks for one deliverable should not need the column filled thirty times.
     */
    defaultLineItemId: zod_1.z.uuid().optional(),
});
/**
 * One row that did not go in.
 *
 * ⚠️⚠️ **`line` IS THE LINE IN THE FILE, counting the header and counting from 1** — so *"14. sor"*
 * means the row somebody sees in Excel. A zero-based index into the data rows is the number that
 * makes an error report useless, and the mockup's own error list is phrased that way.
 */
exports.importErrorSchema = zod_1.z.object({
    line: zod_1.z.number().int().positive(),
    /** Already a sentence, because only the server knows which rule refused it. */
    message: zod_1.z.string(),
});
exports.importResultSchema = zod_1.z.object({
    /** How many data rows the file had. */
    total: zod_1.z.number().int().nonnegative(),
    created: zod_1.z.number().int().nonnegative(),
    updated: zod_1.z.number().int().nonnegative(),
    /**
     * ⚠️ **Skipped is not failed.** The mockup's own wording: *"N sor kimaradt, mert ugyanaz a
     * bejegyzés már szerepelt."* A duplicate is a row that needed nothing done, which is a success —
     * counting it as an error would make a second run of the same file look like a disaster.
     */
    skipped: zod_1.z.number().int().nonnegative(),
    failed: zod_1.z.number().int().nonnegative(),
    /**
     * ⚠️ **Capped, and `failed` is the real count.** An import of 2,000 broken rows would otherwise
     * return 2,000 sentences into a dialog that shows five. The error CSV is built from these, so the
     * cap is what the screen can honestly offer to download.
     */
    errors: zod_1.z.array(exports.importErrorSchema),
});
/**
 * ⚠️ **Capped at 200 errors.** Enough to see a pattern — a whole column mismapped shows up in the
 * first three — and small enough to put in a dialog and a JSON response.
 */
exports.IMPORT_MAX_ERRORS = 200;
/* ── the columns each domain reads ─────────────────────────────────────────────────────────── */
/**
 * ⚠️⚠️ **EVERY COLUMN HAS ALIASES, AND THAT IS THE EXPORT ROUND TRIP.** The mockup's sample calls a
 * worklog's task `feladatkulcs`; this product's export writes `feladat_kulcs`. Without both names, a
 * file this system produced could not be fed back into it — which is the first thing anybody tries.
 *
 * ⚠️ Lower-case, because `parseCsv` lower-cases the header.
 */
exports.IMPORT_COLUMNS = {
    task: {
        key: ['kulcs', 'key'],
        title: ['cim', 'title'],
        project: ['projekt', 'project'],
        /** P2-32.2 · `HSO-C01/1` — the contract, then the line item's number, as its address reads. */
        lineItem: ['sortetel', 'line_item'],
        assignee: ['felelos', 'assignee'],
        estimate: ['becsles', 'estimate'],
        startsOn: ['kezdes', 'starts_on'],
        dueOn: ['hatarido', 'due_on'],
    },
    worklog: {
        workedOn: ['datum', 'date'],
        taskKey: ['feladatkulcs', 'feladat_kulcs', 'task_key'],
        hours: ['ora', 'hours'],
        note: ['megjegyzes', 'note'],
    },
};
/** Read one logical field out of a parsed row, whichever alias the file used. */
function importField(values, aliases) {
    for (const alias of aliases) {
        const value = values[alias];
        if (value !== undefined && value !== '')
            return value;
    }
    return '';
}
/**
 * The sample file the dialog offers.
 *
 * ⚠️⚠️ **SEMICOLONS, WHICH IS THE MOCKUP'S OWN CHOICE AND THE RIGHT ONE HERE.** Excel with a
 * Hungarian locale reads and writes `;` — a comma sample opened in Excel lands in one column, which
 * makes the sample look broken to exactly the person who needed it. The parser detects either, so
 * offering the one Excel is happiest with costs nothing.
 *
 * ⚠️ The rows are the mockup's, with its own example values.
 */
function importSample(domain) {
    if (domain === 'task') {
        return [
            /* ⚠️ P2-32.2 · `sortetel`: a new task needs a line item, and a sample whose new-task row
               could never succeed would teach the wrong file. An update row leaves it empty. */
            'kulcs;cim;projekt;sortetel;felelos;becsles;kezdes;hatarido',
            'HSO-142;Proof of Completion;HSO;;gabor.takacs@loginet.hu;8:00;2026-08-24;2026-09-01',
            ';Új feladat leírás;ROSS;ROSS-C01/1;leo.kussy@loginet.hu;4:30;2026-08-25;2026-08-28',
            '',
        ].join('\r\n');
    }
    return [
        'datum;feladatkulcs;ora;megjegyzes',
        '2026-08-17;ROSS-1043;3:00;Gantt Q4 szakasz',
        '2026-08-18;HSO-142;2:30;PoC előkészítés',
        '',
    ].join('\r\n');
}
//# sourceMappingURL=contract.js.map